
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface QuotationsPaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export function QuotationsPagination({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
}: QuotationsPaginationProps) {
  const isMobile = useIsMobile();
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  if (totalPages <= 1) return null;
  
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };
  
  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const maxPageButtons = isMobile ? 3 : 5;
    const pages = [];
    
    // If few pages, show all
    if (totalPages <= maxPageButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    // For many pages, show a window around the current page
    const halfWindow = Math.floor(maxPageButtons / 2);
    let start = currentPage - halfWindow;
    let end = currentPage + halfWindow;
    
    // Adjust window if at the start
    if (start < 1) {
      end = Math.min(end + (1 - start), totalPages);
      start = 1;
    }
    
    // Adjust window if at the end
    if (end > totalPages) {
      start = Math.max(start - (end - totalPages), 1);
      end = totalPages;
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  };
  
  return (
    <div className="flex items-center justify-center gap-1 mt-4">
      <Button 
        variant="outline"
        size={isMobile ? "sm" : "default"}
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className="border-gray-700 text-white hover:bg-[#252A38] hover:text-white"
      >
        <ChevronLeft className="h-4 w-4" />
        {!isMobile && <span className="ml-1">Anterior</span>}
      </Button>
      
      {getPageNumbers().map((page) => (
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size={isMobile ? "sm" : "default"}
          onClick={() => onPageChange(page)}
          className={currentPage === page 
            ? "bg-[#0073B7] hover:bg-[#004080] text-white" 
            : "border-gray-700 text-white hover:bg-[#252A38] hover:text-white"
          }
        >
          {page}
        </Button>
      ))}
      
      <Button 
        variant="outline"
        size={isMobile ? "sm" : "default"}
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className="border-gray-700 text-white hover:bg-[#252A38] hover:text-white"
      >
        {!isMobile && <span className="mr-1">Próximo</span>}
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
